import { useTranslation } from "react-i18next";

import BaseProgressBar from "@/components_v2/ProgressBars/BaseProgressBar";
import BaseTag from "@/components_v2/Tags/BaseTag";
import { TAG_VARIANTS } from "@/components_v2/Tags/constants";
import { getPathCopy } from "@/domains/certifications/utils";
import CertificatePanel from "@/features/certifications/overview/components/CertificatePanel";
import { PathProgressCardProps } from "@/features/certifications/overview/components/types";
import { getCommunityRequirement } from "@/features/certifications/overview/utils";
import {
  ISSUANCE_STATES,
  MODULE_STATUSES,
} from "@/domains/certifications/constants";

import "./styles.css";

const PROGRESS_STROKE_WIDTH = 5;

const PathProgressCard = ({
  track,
  weeks,
  contentKey,
  programmeTitle,
  credential,
  onSwitchPath,
}: PathProgressCardProps) => {
  const { t } = useTranslation();
  const copy = getPathCopy(t, contentKey, track);

  const completed = weeks.filter(
    (week) => week.status === MODULE_STATUSES.COMPLETE,
  ).length;
  const total = weeks.length;
  const isFinished = total > 0 && completed === total;
  const current = weeks.find((week) => week.status === MODULE_STATUSES.CURRENT);
  const hasStarted = weeks.some((week) => week.isStarted);
  const requirementSentence = t(
    `${contentKey}.paths.${track.slug}.requirementSentence`,
    {
      community:
        getCommunityRequirement(t, weeks) ??
        t("certification.overview.communityFallback"),
      defaultValue: copy.requirementSentence,
    },
  );

  let helpText: string | undefined;

  if (!isFinished && current) {
    helpText = hasStarted
      ? t("certification.overview.weekInProgress", {
          position: current.position,
        })
      : t("certification.overview.notStarted");
  }

  return (
    <div className="certification-card path-progress-card flex flex-col gap-400">
      <div className="flex flex-wrap items-center justify-between gap-300">
        <div className="flex flex-wrap items-center gap-300">
          <BaseTag
            label={copy.title}
            size="small"
            variant={TAG_VARIANTS.TINTED}
          />

          <span className="certification-muted body-s-regular">
            {isFinished
              ? t("certification.overview.completed")
              : t("certification.overview.chosenPath")}
          </span>
        </div>

        {onSwitchPath && (
          <button
            type="button"
            className="certification-link-button body-s-medium"
            onClick={onSwitchPath}
          >
            {t("certification.overview.switchPath")}
          </button>
        )}
      </div>

      <BaseProgressBar
        value={completed}
        total={Math.max(total, 1)}
        label={t("certification.overview.weeksComplete", { completed, total })}
        helpText={helpText}
        description={isFinished ? undefined : requirementSentence}
        descriptionAlign="left"
        strokeWidth={PROGRESS_STROKE_WIDTH}
        strokeColor="var(--orange-300)"
      />

      {credential?.state === ISSUANCE_STATES.ISSUED &&
        credential.track === track.id && (
          <CertificatePanel
            credential={credential}
            certificateTitle={t("certification.overview.certificate.title", {
              path: copy.shortTitle,
            })}
            programmeTitle={programmeTitle}
          />
        )}
    </div>
  );
};

export default PathProgressCard;
